// client/src/contexts/PortfolioContext.tsx
"use client";

import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { apiRequest } from '@/utils/api';
import { useAuth } from './AuthContext';
import { useQuery, useQueryClient, QueryClientProvider } from '@tanstack/react-query';
import { QueryClient } from '@tanstack/query-core';
import Cookies from 'js-cookie';
import { Portfolio } from '@/types';



interface PortfolioContextType {
  portfolios: Portfolio[];
  activePortfolio: Portfolio | null;
  loading: boolean;
  setActivePortfolio: (portfolio: Portfolio | null) => void;
  switchPortfolio: (portfolioOrId: Portfolio | string | number) => Promise<void>;
  loadPortfolios: () => Promise<void>;
  createPortfolio: (portfolioData: Record<string, unknown>) => Promise<Portfolio>;
  deletePortfolio: (id: string) => Promise<boolean>;
  updatePortfolio: (id: string, portfolioData: Record<string, unknown>) => Promise<Portfolio>;
  dropdownOpen: boolean;
  setDropdownOpen: (isOpen: boolean) => void;
  isInitialized: boolean;
  refreshPortfolios: () => Promise<void>;
  activePortfolioId: string | undefined;
  sharePortfolio: (portfolioId: string, userEmail: string, permission: string) => Promise<unknown>;
  removeShare: (portfolioId: string, userId: string) => Promise<void>;
  queryClient: QueryClient;
}

interface PortfolioProviderProps {
  children: React.ReactNode;
  initialPortfolios?: Portfolio[];
  initialActivePortfolio?: Portfolio | null;
}

// Global variable to store the interceptor ID and current portfolio ID
let portfolioInterceptor: number | null = null;
export const currentPortfolioIdRef = { current: null as string | null }; // Export for testing

export const setupAxiosInterceptors = () => {
  if (portfolioInterceptor !== null) {
    axios.interceptors.request.eject(portfolioInterceptor);
  }

  portfolioInterceptor = axios.interceptors.request.use(
    (config) => {
      // Skip for specific endpoints that don't need auth headers
      if (config.url?.endsWith('/portfolios') ||
        config.url?.includes('/auth/')) {
        return config;
      }

      // Get the current portfolio ID from ref (updated synchronously)
      const portfolioId = currentPortfolioIdRef.current;
      
      // Add the portfolio ID to the headers
      if (portfolioId && config.url?.startsWith('/api/')) {
        config.headers = config.headers || {};
        config.headers['x-portfolio-id'] = portfolioId;
      }

      // Add impersonation header if there's an active impersonation session
      if (config.url?.startsWith('/api/')) {
        try {
          const impersonationSession = localStorage.getItem('impersonation_session');
          if (impersonationSession) {
            const session = JSON.parse(impersonationSession);
            if (session?.target_user_id) {
              config.headers = config.headers || {};
              config.headers['x-impersonated-user-id'] = session.target_user_id;
            }
          }
        } catch (_e) {
          // Ignore localStorage errors
        }
      }

      return config;
    },
    error => {
      console.error('Request interceptor error:', error);
      return Promise.reject(error);
    }
  );
};

// Initialize the interceptor once when the module loads
setupAxiosInterceptors();

// Create a query client for the app
const createQueryClient = () => new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error: unknown) => {
        // Don't retry on 4xx errors except 408, 429
        if (((error as { response?: { status?: number } })?.response?.status ?? 0) >= 400 &&
            ((error as { response?: { status?: number } })?.response?.status ?? 0) < 500 &&
            ![408, 429].includes((error as { response?: { status?: number } })?.response?.status ?? 0)) {
          return false;
        }
        return failureCount < 3;
      },
      staleTime: 5 * 60 * 1000, // 5 minutes default
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
      gcTime: 10 * 60 * 1000, // 10 minutes garbage collection
    },
    mutations: {
      retry: 1,
    },
  },
});

const PortfolioContext = createContext<PortfolioContextType | undefined>(undefined);

// Portfolio Provider Component
export function PortfolioProvider({ 
  children, 
  initialPortfolios = [], 
  initialActivePortfolio = null 
}: PortfolioProviderProps) {
  const { user, loading: authLoading } = useAuth();
  // Tracks whether this session has ever seen an authenticated user, so a
  // transient null during auth resolution is not mistaken for a logout.
  const hasHadUserRef = useRef(false);
  useEffect(() => {
    if (user) hasHadUserRef.current = true;
  }, [user]);
  const queryClient = useQueryClient();
  const [activePortfolio, setActivePortfolioState] = useState<Portfolio | null>(() => {
    return initialActivePortfolio || ( (typeof window !== 'undefined') ? JSON.parse(localStorage.getItem('activePortfolio') || 'null') : null) || null;
  });

  const [dropdownOpen, setDropdownOpen] = useState(false);

  // Track impersonation status for queryKey - this ensures refetch when impersonation changes
  const [isImpersonating, setIsImpersonating] = useState(false);

  // Effect to update impersonation state
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const checkImpersonation = () => {
        const session = localStorage.getItem('impersonation_session');
        setIsImpersonating(!!session);
      };
      
      checkImpersonation();
      
      // Listen for storage changes (handles page reload with new impersonation state)
      window.addEventListener('storage', checkImpersonation);
      return () => window.removeEventListener('storage', checkImpersonation);
    }
  }, []);

  // React Query for portfolios - queryKey includes impersonation status to force refetch when it changes
  const { data: portfolios = [], isLoading, error: _error, refetch } = useQuery({
    queryKey: ['portfolios', isImpersonating],
    queryFn: async () => {
      // isImpersonating is now tracked in state, so we don't need to check localStorage here
      // If we have initial server-side data, user is authenticated, and NOT impersonating, use that
      if (initialPortfolios.length > 0 && user && !isImpersonating) {
        console.log('[Portfolio] Using server-side portfolios data:', initialPortfolios.length);
        console.log(['Portfolio] Portfolios: ', initialPortfolios])
        return initialPortfolios;
      }
      
      // If impersonating or no initial data, fetch from API
      if (!user) throw new Error('User not authenticated');
      
      console.log('[Portfolio] Fetching portfolios from API...');
      try {
        const data = await apiRequest<Portfolio[]>(
          'GET',
          '/portfolios',
          undefined,
          undefined,
          undefined,
          process.env.NODE_ENV === 'development' ? 30000 : 10000
        );

        console.log('[Portfolio] Fetched portfolios:', data.length);
        console.log('[Portfolio] Portfolios: ', data)
        return data;
      } catch (error) {
        const axiosError = error as { response?: { status?: number } };
        if (axiosError.response?.status === 401) {
          console.error('[Portfolio] 401 Unauthorized when fetching portfolios - auth may still be settling');
          // Don't throw - return empty array to avoid infinite retry loops
          // The DashboardContext will handle the case where no portfolios are found
          return [];
        }
        throw error;
      }
    },
    enabled: !!user, // Query is enabled for all authenticated users; queryFn handles impersonation dynamically
    staleTime: 10 * 60 * 1000, // 10 minutes for portfolios (changes rarely)
    gcTime: 30 * 60 * 1000, // 30 minutes garbage collection
    initialData: !isImpersonating && initialPortfolios.length > 0 ? initialPortfolios : undefined, // Don't use initialData when impersonating
    refetchOnWindowFocus: false, // Prevent refetch on window focus
    refetchOnMount: false, // Prevent refetch on mount if we have initial data
  });

  // Set active portfolio when portfolios are loaded - prioritize default_portfolio_id, then localStorage and preserve during auth settling
  useEffect(() => {
    console.log('[Portfolio] useEffect running:', {
      portfoliosLength: portfolios.length,
      hasInitial: !!initialActivePortfolio,
      currentActive: activePortfolio?.name,
      hasUser: !!user
    });

    if (portfolios.length === 0) return;

    // Check if we're impersonating
    const isImpersonating = typeof window !== 'undefined' && !!localStorage.getItem('impersonation_session');
    console.log('[Portfolio] useEffect check:', {
      isImpersonating,
      portfolioCount: portfolios.length,
      portfolioNames: portfolios.map((p: Portfolio) => ({ name: p.name, isOwner: p.isOwner, owner_id: p.owner_id })),
      currentActiveId: activePortfolio?.id
    });
    
    // If impersonating, always select an owned portfolio from the impersonated user
    if (isImpersonating) {
      console.log('[Portfolio] Impersonating - searching for owned portfolio of impersonated user');
      
      // First, clear any stale portfolio cookies that might conflict
      Cookies.remove('activePortfolioId');
      
      const ownedPortfolio = portfolios.find((p: Portfolio) => p.isOwner);
      console.log('[Portfolio] Owned portfolio found:', ownedPortfolio?.name || 'none');
      if (ownedPortfolio) {
        console.log('[Portfolio] Setting impersonated user owned portfolio:', ownedPortfolio.name, 'id:', ownedPortfolio.id);
        setActivePortfolioState(ownedPortfolio);
        return;
      }
      // If no owned portfolio found, just pick first available
      console.log('[Portfolio] No owned portfolio for impersonated user, using first:', portfolios[0]?.name);
      setActivePortfolioState(portfolios[0]);
      return;
    }

    // If we already have an active portfolio that exists in the portfolios list, keep it
    // This prevents unnecessary switches during auth settling
    if (activePortfolio && portfolios.some((p: Portfolio) => p.id === activePortfolio.id)) {
      console.log('[Portfolio] Keeping existing active portfolio during auth settling:', activePortfolio.name);
      return;
    }

    // First check user's default_portfolio_id from API (highest priority)
    if (user && portfolios.length > 0) {
      const fetchDefaultPortfolio = async () => {
        try {
          const data = await apiRequest<{ default_portfolio?: { id?: string } }>(
            'GET',
            '/user/default-portfolio'
          );

          if (data?.default_portfolio) {
            const defaultPortfolio = portfolios.find((p: Portfolio) => p.id === data.default_portfolio?.id);
            if (defaultPortfolio) {
              console.log('[Portfolio] Setting from user default_portfolio_id:', defaultPortfolio.name);
              setActivePortfolioState(defaultPortfolio);
              return;
            }
          }
        } catch (_error) {
          console.log('[Portfolio] Could not fetch default portfolio, falling back to other methods');
        }
      };
      
      fetchDefaultPortfolio();
    }

    // Then check localStorage (fallback)
    if (typeof window !== 'undefined') {
      const savedPortfolioRaw = localStorage.getItem('activePortfolio');
      console.log('[Portfolio] localStorage check:', savedPortfolioRaw ? 'found' : 'not found');
      if (savedPortfolioRaw) {
        try {
          const parsed = JSON.parse(savedPortfolioRaw);
          const stillExists = portfolios.some((p: Portfolio) => p.id === parsed.id);
          console.log('[Portfolio] localStorage portfolio:', parsed.name, 'exists:', stillExists);
          if (stillExists) {
            console.log('[Portfolio] Setting from localStorage:', parsed.name);
            setActivePortfolioState(parsed);
            return;
          } else {
            // Clean up invalid portfolio from localStorage
            console.log('[Portfolio] Removing invalid portfolio from localStorage:', parsed.name);
            localStorage.removeItem('activePortfolio');
          }
        } catch (_e) {
          if (portfolios.length > 0 ){
            console.log("invalid portfolio found", savedPortfolioRaw, portfolios)
            localStorage.removeItem('activePortfolio');
          }
        }
      }
    }

    // Then check cookies
    const savedPortfolioId = Cookies.get('activePortfolioId');
    if (savedPortfolioId) {
      const savedPortfolio = portfolios.find((p: Portfolio) => p.id === savedPortfolioId);
      if (savedPortfolio) {
        setActivePortfolioState(savedPortfolio);
        return;
      }
    }

    // Fallback to SSR initial
    if (initialActivePortfolio) {
      const stillExists = portfolios.some((p: Portfolio) => p.id === initialActivePortfolio.id);
      if (stillExists) {
        setActivePortfolioState(initialActivePortfolio);
        return;
      }
    }

    // Last resort: first portfolio
    if (!activePortfolio) {
      console.log('[Portfolio] Setting first portfolio as active:', portfolios[0].name);
      setActivePortfolioState(portfolios[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portfolios, initialActivePortfolio, user]); // Added user to dependencies

  // Effect to handle activePortfolio updates and cookie/localStorage sync
  useEffect(() => {
    if (activePortfolio) {
      Cookies.set('activePortfolioId', activePortfolio.id, { expires: 7, path: '/' });
      currentPortfolioIdRef.current = activePortfolio.id;
      axios.defaults.headers.common['X-Portfolio-ID'] = activePortfolio.id;

      // Update localStorage with the current active portfolio
      if (typeof window !== 'undefined') {
        localStorage.setItem('activePortfolio', JSON.stringify(activePortfolio));
        console.log('[Portfolio] Updated localStorage with active portfolio:', activePortfolio.name);
      }
    } else {
      Cookies.remove('activePortfolioId');
      currentPortfolioIdRef.current = null;
      delete axios.defaults.headers.common['X-Portfolio-ID'];

      // Also clear localStorage
      if (typeof window !== 'undefined') {
        localStorage.removeItem('activePortfolio');
      }
    }
  }, [activePortfolio]);

  // Clear portfolio state on sign out - but be more careful during initial auth
  useEffect(() => {
    // Only clear if user was previously authenticated and is now explicitly logged out
    // Don't clear during initial authentication state changes
    if (user === null && activePortfolio !== null) {
      // Genuine logout vs auth still settling. This used to ask whether the
      // `token` cookie had gone, which for a signed-in user was never true, so
      // state was only ever cleared once the cookie actually disappeared.
      //
      // `!authLoading` alone is not equivalent: AuthContext force-clears its
      // loading flag after a 3s timeout, so a slow /api/auth/me leaves
      // authLoading false with user still null - and this would then wipe the
      // active portfolio out from under a signed-in user, leaving dependent
      // fetches (income/expense categories) with nothing to key on.
      //
      // Requiring that we actually observed a user first restores the original
      // meaning: only clear on a transition out of a session we had.
      const isGenuineLogout = !authLoading && hasHadUserRef.current;

      if (isGenuineLogout) {
        console.log('[Portfolio] User logged out, clearing portfolio state');
        setActivePortfolioState(null);
        Cookies.remove('activePortfolioId');
        currentPortfolioIdRef.current = null;
        delete axios.defaults.headers.common['X-Portfolio-ID'];

        // Also clear localStorage
        if (typeof window !== 'undefined') {
          localStorage.removeItem('activePortfolio');
        }
      } else {
        console.log('[Portfolio] Auth state settling, preserving portfolio state');
      }
    }
  }, [user, activePortfolio, authLoading]);

  // Check for OAuth setup flags to set new portfolio as active
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const newPortfolioId = localStorage.getItem('new_portfolio_id');
      const forceRefresh = localStorage.getItem('force_dashboard_refresh');
      
      if (newPortfolioId || forceRefresh) {
        console.log('[Portfolio] OAuth setup detected, forcing portfolio refresh');
        console.log('[Portfolio] OAuth flags:', { newPortfolioId, forceRefresh });
        
        // Clear all cache and force refetch
        queryClient.removeQueries({ queryKey: ['portfolios'] });
        queryClient.invalidateQueries({ queryKey: ['portfolios'] });
        
        // Add a small delay to ensure cache is cleared
        setTimeout(() => {
          refetch().then((result) => {
            console.log('[Portfolio] Force refetch result:', {
              dataLength: result.data?.length || 0,
              portfolioIds: result.data?.map((p: Portfolio) => p.id) || []
            });
          
            // Now try to find and set the new portfolio
            if (result.data && result.data.length > 0) {
              const newPortfolio = result.data.find((p: Portfolio) => p.id === newPortfolioId);
              if (newPortfolio) {
                console.log('[Portfolio] Found new portfolio after refetch:', newPortfolio);
                setActivePortfolioState(newPortfolio);
                
                // Clear OAuth flags - DashboardContext will handle the refresh
                setTimeout(() => {
                  console.log('[Portfolio] Clearing OAuth flags after setting active portfolio');
                  localStorage.removeItem('new_portfolio_id');
                  localStorage.removeItem('new_portfolio_name');
                  // Don't clear force_dashboard_refresh - let DashboardContext handle it
                }, 100);
              } else {
                console.log('[Portfolio] New portfolio not found after refetch, available portfolios:', result.data.map((p: Portfolio) => ({ id: p.id, name: p.name })));
              }
            }
          });
        }, 200); // 200ms delay to ensure cache is cleared
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePortfolio, portfolios, queryClient]); // Removed refetch as it can be unstable

  const switchPortfolio = async (portfolioOrId: Portfolio | string | number) => {
    let portfolio: Portfolio | undefined;

    if (typeof portfolioOrId === 'string' || typeof portfolioOrId === 'number') {
      portfolio = portfolios.find((p: Portfolio) => p.id === portfolioOrId);
      if (!portfolio) {
        console.log('[Portfolio] Portfolio not found with ID:', portfolioOrId);
        return;
      }
    } else if (portfolioOrId && (portfolioOrId as Portfolio).id) {
      portfolio = portfolioOrId as Portfolio;
    } else {
      return;
    }

    // Prevent switching to the same portfolio
    if (activePortfolio && portfolio && activePortfolio.id === portfolio.id) {
      console.log('[Portfolio] Already active portfolio, skipping switch');
      return;
    }

    console.log('[Portfolio] Switching to portfolio:', portfolio.name, portfolio.id);

    // Update state synchronously
    setActivePortfolioState(portfolio);
    setDropdownOpen(false);

    // Update axios interceptor immediately
    currentPortfolioIdRef.current = portfolio.id;
    Cookies.set('activePortfolioId', portfolio.id, { expires: 7 });
    axios.defaults.headers.common['X-Portfolio-ID'] = portfolio.id;

    // Also save to localStorage
    if (typeof window !== 'undefined') {
      localStorage.setItem('activePortfolio', JSON.stringify(portfolio));
      console.log('[Portfolio] Saved to localStorage:', portfolio.name, portfolio.id);
      console.log('[Portfolio] localStorage after save:', localStorage.getItem('activePortfolio'));
    }

    // Invalidate portfolio-dependent queries INCLUDING scenarios
    // Scenarios MUST change when switching portfolios
    queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    queryClient.invalidateQueries({ queryKey: ['transactions'] });
    queryClient.invalidateQueries({ queryKey: ['assets'] });
    queryClient.invalidateQueries({ queryKey: ['expenses'] });
    queryClient.invalidateQueries({ queryKey: ['income'] });
    queryClient.invalidateQueries({ queryKey: ['scenarios'] });

    // Dispatch event for other components to react to portfolio change
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('portfolio-changed'));
    }
  };

  const loadPortfolios = async () => {
    await refetch();
  };

  const createPortfolio = async (portfolioData: Record<string, unknown>) => {
    try {
      const newPortfolio = await apiRequest<Portfolio>('POST', '/portfolios', portfolioData);
      
      // Invalidate portfolios query to refetch
      queryClient.invalidateQueries({ queryKey: ['portfolios'] });
      
      // Switch to the new portfolio
      await switchPortfolio(newPortfolio);
      
      return newPortfolio;
    } catch (_error) {
      throw _error;
    }
  };

  const updatePortfolio = async (id: string, portfolioData: Record<string, unknown>) => {
    try {
      const updatedPortfolio = await apiRequest<Portfolio>('PUT', `/portfolios/${id}`, portfolioData);
      
      // Invalidate portfolios query to refetch
      queryClient.invalidateQueries({ queryKey: ['portfolios'] });

      if (activePortfolio && activePortfolio.id === id) {
        setActivePortfolioState(updatedPortfolio);
      }

      return updatedPortfolio;
    } catch (_error) {
      throw _error;
    }
  };

  const deletePortfolio = async (id: string) => {
    try {
      await apiRequest('DELETE', `/portfolios/${id}`);

      // Move off the deleted portfolio BEFORE anything refetches. The axios
      // interceptor reads currentPortfolioIdRef synchronously, so refetching
      // first sent the deleted id in x-portfolio-id and the server answered
      // 404 "Portfolio not found" (unhandled AxiosError on /subscription).
      if (activePortfolio && activePortfolio.id === id) {
        const remainingPortfolios = portfolios.filter((p: Portfolio) => p.id !== id);
        if (remainingPortfolios.length > 0) {
          await switchPortfolio(remainingPortfolios[0]);
        } else {
          setActivePortfolioState(null);
          currentPortfolioIdRef.current = null;
          delete axios.defaults.headers.common['X-Portfolio-ID'];
          Cookies.remove('activePortfolioId');
          if (typeof window !== 'undefined') {
            localStorage.removeItem('activePortfolio');
          }
        }
      }

      // Invalidate portfolios query to refetch
      queryClient.invalidateQueries({ queryKey: ['portfolios'] });

      return true;
    } catch (_error) {
      throw _error;
    }
  };

  const sharePortfolio = async (portfolioId: string, userEmail: string, permission: string) => {
    try {
      const data = await apiRequest(`POST`, `/portfolios/${portfolioId}/share`, {
        userEmail,
        permission
      });

      // Reload portfolios to get updated sharing info
      await loadPortfolios();

      return data;
    } catch (_error) {
      throw _error;
    }
  };

  const removeShare = async (portfolioId: string, userId: string) => {
    try {
      await apiRequest('DELETE', `/portfolios/${portfolioId}/share/${userId}`);

      // Reload portfolios to get updated sharing info
      await loadPortfolios();
    } catch (_error) {
      throw _error;
    }
  };

  const value: PortfolioContextType = {
    portfolios,
    activePortfolio,
    loading: isLoading,
    setActivePortfolio: setActivePortfolioState,
    switchPortfolio,
    loadPortfolios,
    createPortfolio,
    deletePortfolio,
    updatePortfolio,
    dropdownOpen,
    setDropdownOpen,
    isInitialized: !isLoading,
    refreshPortfolios: loadPortfolios,
    activePortfolioId: activePortfolio?.id,
    sharePortfolio,
    removeShare,
    queryClient
  };

  return (
    <PortfolioContext.Provider value={value}>
      {children}
    </PortfolioContext.Provider>
  );
}

export function usePortfolio() {
  const context = useContext(PortfolioContext);
  if (context === undefined) {
    throw new Error('usePortfolio must be used within a PortfolioProvider');
  }
  return context;
}

// Export a wrapper component that includes QueryClientProvider
export function PortfolioProviderWithQuery({ 
  children, 
  initialPortfolios = [], 
  initialActivePortfolio = null 
}: { 
  children: React.ReactNode;
  initialPortfolios?: Portfolio[];
  initialActivePortfolio?: Portfolio | null;
}) {
  const queryClient = createQueryClient();
  
  return (
    <QueryClientProvider client={queryClient}>
      <PortfolioProvider 
        initialPortfolios={initialPortfolios}
        initialActivePortfolio={initialActivePortfolio}
      >
        {children}
      </PortfolioProvider>
    </QueryClientProvider>
  );
}
